/**
 * Hasura schema-sync races (gql-server rolling deploys, #11360 / #13426).
 *
 * Production runs multiple Hasura replicas that rebuild their in-memory GraphQL
 * schema asynchronously after metadata apply. For a few seconds a request or
 * websocket subscription can hit a replica whose `query_root` / `subscription_root`
 * is missing a permanently-tracked field, which surfaces as:
 *
 *   field 'DynamicField' not found in type: 'subscription_root'
 *   extensions.code === 'validation-failed'
 *
 * Genuine document bugs look like `field 'foo' not found in type: 'Customer'`
 * and must still be reported — only `*_root` misses are treated as transient.
 */

export const HASURA_SCHEMA_SYNC_RETRY_DELAY_MS = 1500;
export const HASURA_SCHEMA_SYNC_RETRY_COUNT = 2;
export const HAS_BEARER_TOKEN_CONTEXT_KEY = 'hasBearerToken';

const ROOT_FIELD_NOT_FOUND =
  /field '[^']+' not found in type: '(query|subscription|mutation)_root'/i;

export function isTransientHasuraSchemaSyncError(error: unknown): boolean {
  if (error == null) {
    return false;
  }
  if (typeof error === 'string') {
    return ROOT_FIELD_NOT_FOUND.test(error);
  }
  if (typeof error !== 'object') {
    return false;
  }

  const obj = error as Record<string, unknown>;

  if (typeof obj['message'] === 'string' && ROOT_FIELD_NOT_FOUND.test(obj['message'])) {
    return true;
  }

  if (Array.isArray(obj['graphQLErrors'])) {
    if (obj['graphQLErrors'].some(isTransientHasuraSchemaSyncError)) {
      return true;
    }
  }

  if (Array.isArray(obj['errors'])) {
    if (obj['errors'].some(isTransientHasuraSchemaSyncError)) {
      return true;
    }
  }

  if (obj['networkError'] != null && isTransientHasuraSchemaSyncError(obj['networkError'])) {
    return true;
  }

  return false;
}

/**
 * Anonymous-role root-field misses have the same response text as a real
 * schema-cache miss. Only operations positively marked as carrying a Bearer
 * token may use the schema-sync retry/suppression path.
 */
export function isAuthenticatedHasuraSchemaSyncError(
  error: unknown,
  operationContext: Record<string, unknown>
): boolean {
  return operationContext[HAS_BEARER_TOKEN_CONTEXT_KEY] === true
    && isTransientHasuraSchemaSyncError(error);
}
